"use client";

import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { Badge } from "../../";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/collapsible/Collapsible";
import { cn } from "../../utils";
import { IconBrain, IconChevronDown, IconCircleDot, type Icon } from "@tabler/icons-react";
import type { ComponentProps, ReactNode } from "react";
import { createContext, memo, useContext, useMemo } from "react";

interface ChainOfThoughtContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(null);

const useChainOfThought = () => {
  const context = useContext(ChainOfThoughtContext);
  if (!context) {
    throw new Error("ChainOfThought components must be used within ChainOfThought");
  }
  return context;
};

export type ChainOfThoughtProps = ComponentProps<"div"> & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const ChainOfThought = memo(
  ({
    className,
    open,
    defaultOpen = false,
    onOpenChange,
    children,
    ...props
  }: ChainOfThoughtProps) => {
    const [isOpen, setIsOpen] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    });

    const chainOfThoughtContext = useMemo(() => ({ isOpen, setIsOpen }), [isOpen, setIsOpen]);

    return (
      <ChainOfThoughtContext.Provider value={chainOfThoughtContext}>
        <div className={cn("al-not-prose al-max-w-prose al-space-y-4", className)} {...props}>
          {children}
        </div>
      </ChainOfThoughtContext.Provider>
    );
  }
);

export type ChainOfThoughtHeaderProps = ComponentProps<typeof CollapsibleTrigger>;

export const ChainOfThoughtHeader = memo(
  ({ className, children, ...props }: ChainOfThoughtHeaderProps) => {
    const { isOpen, setIsOpen } = useChainOfThought();

    return (
      <Collapsible onOpenChange={setIsOpen} open={isOpen}>
        <CollapsibleTrigger
          className={cn(
            "al-flex al-w-full al-items-center al-gap-2 al-text-muted-foreground al-text-sm al-transition-colors hover:al-text-foreground",
            className
          )}
          {...props}
        >
          <IconBrain className="al-size-4" />
          <span className="al-flex-1 al-text-left">{children ?? "Assistant"}</span>
          <IconChevronDown
            className={cn(
              "al-size-4 al-transition-transform",
              isOpen ? "al-rotate-180" : "al-rotate-0"
            )}
          />
        </CollapsibleTrigger>
      </Collapsible>
    );
  }
);

export type ChainOfThoughtStepProps = ComponentProps<"div"> & {
  icon?: Icon;
  label: ReactNode;
  description?: ReactNode;
  status?: "complete" | "active" | "pending";
};

export const ChainOfThoughtStep = memo(
  ({
    className,
    icon: Icon = IconCircleDot,
    label,
    description,
    status = "complete",
    children,
    ...props
  }: ChainOfThoughtStepProps) => {
    const statusStyles = {
      complete: "text-muted-foreground",
      active: "text-foreground",
      pending: "text-muted-foreground/50",
    };

    return (
      <div
        className={cn(
          "al-flex al-gap-2 al-text-sm",
          statusStyles[status],
          "al-fade-in-0 al-slide-in-from-top-2 al-animate-in",
          className
        )}
        {...props}
      >
        <div className="al-relative al-mt-[0.25em]">
          <Icon className="al-size-4" />
          <div className="al-absolute al-top-7 al-bottom-0 al-left-1/2 al--mx-px al-w-px al-bg-border" />
        </div>
        <div className="al-flex-1 al-space-y-2 al-overflow-hidden">
          <div>{label}</div>
          {description && <div className="al-text-muted-foreground al-text-xs">{description}</div>}
          {children}
        </div>
      </div>
    );
  }
);

export type ChainOfThoughtSearchResultsProps = ComponentProps<"div">;

export const ChainOfThoughtSearchResults = memo(
  ({ className, ...props }: ChainOfThoughtSearchResultsProps) => (
    <div className={cn("al-flex al-flex-wrap al-items-center al-gap-2", className)} {...props} />
  )
);

export type ChainOfThoughtSearchResultProps = ComponentProps<typeof Badge>;

export const ChainOfThoughtSearchResult = memo(
  ({ className, children, ...props }: ChainOfThoughtSearchResultProps) => (
    <Badge
      className={cn("al-gap-1 al-px-2 al-py-0.5 al-font-normal al-text-xs", className)}
      variant="secondary"
      {...props}
    >
      {children}
    </Badge>
  )
);

export type ChainOfThoughtContentProps = ComponentProps<typeof CollapsibleContent>;

export const ChainOfThoughtContent = memo(
  ({ className, children, ...props }: ChainOfThoughtContentProps) => {
    const { isOpen } = useChainOfThought();

    return (
      <Collapsible open={isOpen}>
        <CollapsibleContent
          className={cn(
            "al-mt-2 al-space-y-3",
            "data-[state=closed]:al-fade-out-0 data-[state=closed]:al-slide-out-to-top-2 data-[state=open]:al-slide-in-from-top-2 al-text-popover-foreground al-outline-none data-[state=closed]:al-animate-out data-[state=open]:al-animate-in",
            className
          )}
          {...props}
        >
          {children}
        </CollapsibleContent>
      </Collapsible>
    );
  }
);

export type ChainOfThoughtImageProps = ComponentProps<"div"> & {
  caption?: string;
};

export const ChainOfThoughtImage = memo(
  ({ className, children, caption, ...props }: ChainOfThoughtImageProps) => (
    <div className={cn("al-mt-2 al-space-y-2", className)} {...props}>
      <div className="al-relative al-flex al-max-h-[22rem] al-items-center al-justify-center al-overflow-hidden al-rounded-lg al-bg-muted al-p-3">
        {children}
      </div>
      {caption && <p className="al-text-muted-foreground al-text-xs">{caption}</p>}
    </div>
  )
);

ChainOfThought.displayName = "ChainOfThought";
ChainOfThoughtHeader.displayName = "ChainOfThoughtHeader";
ChainOfThoughtStep.displayName = "ChainOfThoughtStep";
ChainOfThoughtSearchResults.displayName = "ChainOfThoughtSearchResults";
ChainOfThoughtSearchResult.displayName = "ChainOfThoughtSearchResult";
ChainOfThoughtContent.displayName = "ChainOfThoughtContent";
ChainOfThoughtImage.displayName = "ChainOfThoughtImage";
